/**
 * Maps a `seller-service-price` JSON:API listing into the display catalogue tree (ticket #5598).
 * Pure and framework-free, so it is unit-tested in isolation.
 *
 * Each row prices a service and names its position through three relationships, each carrying a
 * stable resource id and resolved to a display title against the document's `included` resources:
 *   - `category` always → the category the row belongs to.
 *   - `sub1Service` → the sub1 service, or empty `data` when the row prices the whole category.
 *   - `sub2Detail` → the sub2 detail, or empty `data` when the form asks no detail.
 *
 * So a row lands at the deepest level its relationships describe:
 *   - category only (no `sub1Service`) → prices the whole category (rendered as a single row
 *     labeled with the category title, since the UI has no category-level price slot).
 *   - category and service → the sub1 row carries the price.
 *   - category + service + detail → the sub2 row carries the price, nested under its sub1.
 *
 * Grouping is keyed by the relationship **id** (so services with the same title stay distinct),
 * with the resolved title used only as the display label. The catalogue ids exposed to the UI are
 * synthesized from those real relationship ids (DOM-safe) purely for React keys / `Collapsible`
 * ids — never ordinal counters, so ids stay stable across reorders and refetches:
 *   - category → `cat-{id}`.
 *   - sub1 service → `svc-{id}` for a standard `choice-value`, `custom-svc-{id}` for a
 *     `custom-service` (the type prefix keeps ids from colliding, since ids are only unique
 *     within a resource type).
 *   - sub2 detail → `dtl-{id}` for a standard `choice-value`, `custom-dtl-{id}` for a
 *     `custom-detail`.
 * Row insertion order is preserved throughout.
 */

import {
  CatalogueCategory,
  CatalogueService,
  SellerServicePriceAttributes,
  SellerServicePriceDocument,
  ServicePrice,
  ServicePriceIncludedResource,
  ServicePriceRelationship
} from './types';

const toNumber = (value?: string | null): number | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const parsed = Number(value);

  return Number.isNaN(parsed) ? undefined : parsed;
};

const mapPrice = (attrs: SellerServicePriceAttributes): ServicePrice => ({
  priceType: attrs.priceType,
  priceUnit: attrs.priceUnit,
  // NaN when unparseable — the formatter guards and renders the row label-only.
  amount: toNumber(attrs.amount) ?? Number.NaN,
  amountMax: toNumber(attrs.amountMax)
});

/** Indexes sideloaded label resources by `type:id` for O(1) relationship resolution. */
const indexIncluded = (included: Array<ServicePriceIncludedResource> = []): Map<string, string> => {
  const titleByRef = new Map<string, string>();
  for (const resource of included) {
    titleByRef.set(`${resource.type}:${resource.id}`, resource.attributes.title);
  }

  return titleByRef;
};

/** A relationship resolved to its stable resource `type`/`id` and its sideloaded display `title`. */
interface ResolvedRef {
  type: string;
  id: string;
  title: string;
}

/** Resolves a relationship to its `{ type, id, title }`, or `undefined` when the link is empty. */
const resolveRef = (titleByRef: Map<string, string>, relationship?: ServicePriceRelationship): ResolvedRef | undefined => {
  const linkage = relationship?.data;
  if (!linkage) {
    return undefined;
  }

  return { type: linkage.type, id: linkage.id, title: titleByRef.get(`${linkage.type}:${linkage.id}`) ?? '' };
};

/** Resource-type → catalogue-id prefix for a sub1 service row; `custom-service` gets a distinct prefix. */
const SUB1_ID_PREFIX: Record<string, string> = {
  'choice-value': 'svc',
  'custom-service': 'custom-svc'
};

/** Resource-type → catalogue-id prefix for a sub2 detail row; `custom-detail` gets a distinct prefix. */
const SUB2_ID_PREFIX: Record<string, string> = {
  'choice-value': 'dtl',
  'custom-detail': 'custom-dtl'
};

/** DOM-safe catalogue id for a category, keyed by its real relationship id. */
const categoryCatalogueId = (category: ResolvedRef): string => `cat-${category.id}`;

/** DOM-safe catalogue id for a sub1 service — type-prefixed so cross-type ids never collide. */
const sub1CatalogueId = (ref: ResolvedRef): string => `${SUB1_ID_PREFIX[ref.type] ?? 'svc'}-${ref.id}`;

/** DOM-safe catalogue id for a sub2 detail — type-prefixed so cross-type ids never collide. */
const sub2CatalogueId = (ref: ResolvedRef): string => `${SUB2_ID_PREFIX[ref.type] ?? 'dtl'}-${ref.id}`;

/** An ordered category bucket: its synthesized React-key id, display label, and sub1 services. */
interface CategoryBucket {
  catalogueId: string;
  label: string;
  /** sub1 services keyed by their synthesized catalogue id (or the whole-category id). */
  services: Map<string, CatalogueService>;
}

/**
 * Groups the price rows into the ordered `Category → sub1 → sub2` tree consumed by
 * {@link ServicesCatalogue}, keyed by relationship id. Row insertion order is preserved.
 */
export const mapServicePricesToCatalogue = (doc: SellerServicePriceDocument): { categories: Array<CatalogueCategory> } => {
  const titleByRef = indexIncluded(doc.included);
  // Ordered category buckets keyed by their synthesized catalogue id, each holding its sub1
  // services keyed by their own synthesized catalogue id.
  const categoryOrder: Array<string> = [];
  const buckets: Record<string, CategoryBucket> = {};

  const ensureCategory = (category: ResolvedRef): CategoryBucket => {
    const catalogueId = categoryCatalogueId(category);
    if (!buckets[catalogueId]) {
      buckets[catalogueId] = { catalogueId, label: category.title, services: new Map() };
      categoryOrder.push(catalogueId);
    }

    return buckets[catalogueId];
  };

  const ensureSub1 = (bucket: CategoryBucket, serviceId: string, label: string): CatalogueService => {
    const existing = bucket.services.get(serviceId);
    if (existing) {
      return existing;
    }
    const created: CatalogueService = {
      serviceId,
      categoryId: bucket.catalogueId,
      level: 'sub1',
      label
    };
    bucket.services.set(serviceId, created);

    return created;
  };

  for (const row of doc.data) {
    const relationships = row.relationships;
    const category = resolveRef(titleByRef, relationships?.category);
    if (!category) {
      // The category link is present by contract — skip a row that lacks it rather than crash.
      continue;
    }

    const bucket = ensureCategory(category);
    const price = mapPrice(row.attributes);
    const sub1Ref = resolveRef(titleByRef, relationships?.sub1Service);
    const sub2Ref = resolveRef(titleByRef, relationships?.sub2Detail);

    if (!sub1Ref) {
      // Whole-category price: synthesize a single sub1 row labeled with the category title. Its id
      // derives from the category id, so it stays stable and distinct from any real service id.
      const wholeCategory = ensureSub1(bucket, `${bucket.catalogueId}-whole`, category.title);
      wholeCategory.price = price;
      continue;
    }

    const sub1 = ensureSub1(bucket, sub1CatalogueId(sub1Ref), sub1Ref.title);

    if (!sub2Ref) {
      // sub1-level price.
      sub1.price = price;
      continue;
    }

    // sub2-level price nested under its sub1.
    const child: CatalogueService = {
      serviceId: sub2CatalogueId(sub2Ref),
      categoryId: bucket.catalogueId,
      level: 'sub2',
      label: sub2Ref.title,
      price
    };
    sub1.children = [...(sub1.children ?? []), child];
  }

  const categories: Array<CatalogueCategory> = categoryOrder.map(catalogueId => {
    const bucket = buckets[catalogueId];

    return {
      categoryId: bucket.catalogueId,
      label: bucket.label,
      services: Array.from(bucket.services.values())
    };
  });

  return { categories };
};
